/**
 * Scrolls the element with `targetId` into view now, and lines it up again
 * once the page has settled. Returns the cleanup.
 *
 * Why twice (2026-09-24): the first scroll aims at where the target is on the
 * first frame, but the web fonts then swap in and shrink everything above it,
 * and the console is still in its y:30 fade-up, so /#director-console
 * (completion emails, /corrido, the gallery's resume link) ended a couple of
 * hundred px past the console's header. 900ms covers the console's 0.25s
 * delay + 0.6s entrance. 'instant' because html has scroll-behavior:smooth,
 * and a smooth scroll here would aim at a moving target all over again.
 * Skipped once the visitor scrolls, taps or types: from then on they're
 * driving.
 *
 * The first scroll jumps too (2026-09-25). Its one caller is the credits
 * section, and a credits visit is always an arrival (Buy credits in
 * Telegram, back from Checkout, the sign-up resume). On the redesigned
 * landing page ScrollTrigger's refreshes (the hero's fly-through, the pinned
 * "how it works") scroll to 0 to measure and back, which kills a smooth
 * scroll in flight: a phone sat on the hero for ~4s, until the re-align.
 * Index.tsx jumps on arrival for the same reason.
 */
export function scrollToSettled(targetId: string, block: ScrollLogicalPosition = 'start'): () => void {
  const element = document.getElementById(targetId);

  if (element instanceof HTMLElement) {
    element.scrollIntoView({
      behavior: 'instant',
      block,
    });
  }

  let alive = true;
  let timer: number | undefined;
  const stop = () => { alive = false; };
  const intents = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
  intents.forEach((type) => window.addEventListener(type, stop, { passive: true, once: true }));
  let fontsReady: Promise<unknown> = Promise.resolve();
  try {
    fontsReady = document.fonts?.ready ?? Promise.resolve();
  } catch { /* no Font Loading API: settle on the timer alone */ }
  void fontsReady.catch(() => undefined).then(() => {
    if (!alive) return;
    timer = window.setTimeout(() => {
      if (!alive) return;
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'instant', block });
    }, 900);
  });
  return () => {
    alive = false;
    window.clearTimeout(timer);
    intents.forEach((type) => window.removeEventListener(type, stop));
  };
}
